import type { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

const useStyles = makeStyles(() => ({
    root: {
        fontSize: '1.1rem',
        lineHeight: 1.4,
        minWidth: '180px',
    },
    row: {
        marginBottom: '2px',
    },
    label: {
        fontWeight: 600,
    },
    placeholder: {
        fontStyle: 'italic',
        opacity: 0.85,
    },
}))

interface TooltipRow {
    label: string
    value: string
}

const buildRows = (
    bp: BestPracticeData
): { paramRows: TooltipRow[]; extraRows: TooltipRow[] } => {
    const paramRows: TooltipRow[] = (bp.parameters || [])
        .map((p) => {
            const label = p.modifierList?.name?.trim()
            const values = (p.selectedModifiers || [])
                .map((m) => m.name)
                .filter(Boolean)
                .join(', ')
            if (!label || !values) return null
            return { label, value: values }
        })
        .filter((r): r is TooltipRow => Boolean(r))

    const extraRows: TooltipRow[] = []
    if (bp.projectGroup) {
        extraRows.push({ label: 'Project Group', value: bp.projectGroup })
    }
    const loc = bp.location || {}
    const locationParts = [loc.city, loc.region, loc.country].filter(Boolean)
    if (locationParts.length > 0) {
        extraRows.push({ label: 'Location', value: locationParts.join(', ') })
    }

    return { paramRows, extraRows }
}

// Count of badge-eligible rows: parameter rows only (project group + location
// are shown in the tooltip but excluded from the count by design).
export const countParameterRows = (
    bp: BestPracticeData | null | undefined
): number => {
    if (!bp) return 0
    return buildRows(bp).paramRows.length
}

interface BPTooltipContentProps {
    data: BestPracticeData | null
    loading: boolean
    error: boolean
}

export const BPTooltipContent = ({
    data,
    loading,
    error,
}: BPTooltipContentProps) => {
    const classes = useStyles()

    if (loading) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>Loading…</span>
            </div>
        )
    }
    if (error) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>Failed to load</span>
            </div>
        )
    }
    if (!data) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>
                    No Best Practice details
                </span>
            </div>
        )
    }

    const { paramRows, extraRows } = buildRows(data)
    const allRows = [...extraRows, ...paramRows]

    if (allRows.length === 0) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>
                    Best Practice has no parameters
                </span>
            </div>
        )
    }

    return (
        <div className={classes.root}>
            {allRows.map((row) => (
                <div key={row.label} className={classes.row}>
                    <span className={classes.label}>{row.label}:</span>{' '}
                    {row.value}
                </div>
            ))}
        </div>
    )
}
